import { useMemo } from 'react'
import { NoData } from 'src/components/analytics/common/NoData'
import BreakdownTable, { BreakdownItem } from 'src/components/analytics/tables/BreakdownTable'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals, percentageWithFixedDecmials } from 'src/lib/utils/analytics'
import { ChartSkeleton } from '../common/Skeleton'

const ORDER_BREAKDOWN_KEYS = {
  orders: 'Net orders',
  orders_placed: 'Orders placed',
  orders_cancelled: 'Cancelled',
  orders_refunded: 'Returned'
}

type OrderBreakdownRawData = {
  dimension: 'orders' | 'orders_placed' | 'orders_cancelled' | 'orders_refunded'
  data: {
    x: number
  }[]
}[]

const OrdersBreakDownTable = () => {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.orders_breakdown,
      dependencies: {
        endpoints: ['/shopflo-orders-count', '/order-details']
      }
    },
    refreshKey
  })
  const chartBreakdownData: BreakdownItem[] = useMemo(() => {
    const rawData: OrderBreakdownRawData = data[0]?.data?.result?.widgetData?.data
    const comparedData: OrderBreakdownRawData = data[0]?.data?.result?.widgetData?.compared_data

    const totalOrdersData = data[1]?.data?.result?.widgetData?.data?.total_orders || 0
    const totalOrdersComparedData = data[1]?.data?.result?.widgetData?.compared_data?.total_orders || 0

    const mappedData = rawData?.map(item => {
      return {
        label: ORDER_BREAKDOWN_KEYS[item.dimension],
        value: formatNumberToTwoDecimals(item.data[0].x)
      }
    })

    const sortedMappedData = mappedData?.sort((a, b) => {
      if (a.label === 'Net orders') return -1
      if (b.label === 'Net orders') return 1
      return 0
    })

    const comparedMappedData = comparedData?.map(item => {
      return {
        label: ORDER_BREAKDOWN_KEYS[item.dimension],
        value: formatNumberToTwoDecimals(item.data[0].x)
      }
    })

    const sortedComparedMappedData = comparedMappedData?.sort((a, b) => {
      if (a.label === 'Net orders') return -1
      if (b.label === 'Net orders') return 1
      return 0
    })

    if (sortedComparedMappedData && sortedComparedMappedData.length) {
      return sortedMappedData?.map((item, index) => {
        return {
          ...item,
          change:
            item.label === 'Orders placed'
              ? percentageWithFixedDecmials(totalOrdersData, totalOrdersComparedData)
              : percentageWithFixedDecmials(item.value, sortedComparedMappedData[index]?.value || 0),
          bold: index === 0,
          reverseComparison: item.label === 'Returned' || item.label === 'Cancelled'
        }
      })
    }

    return sortedMappedData?.map(item => {
      return {
        label: item.label,
        value: item.value,
        change: 0,
        bold: item.label === 'Net orders'
      }
    })
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (chartBreakdownData.length > 0) {
      return (
        <div className='h-full pt-4'>
          <BreakdownTable data={chartBreakdownData.slice(1, chartBreakdownData.length)} />
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}

export default OrdersBreakDownTable
